// The dock's view of the song on the page: the chords in the lyrics, in the
// same order (and with the same positions) as the chord sequence the backing
// track follows, the lines they're on, and the part the player selected.

import { parseChord } from '../backing-track/chords';

const SONG_SELECTOR = '.song-container .song';

// Chord elements in reading order, skipping symbols that aren't chords (e.g.
// "2x"), so that element i is position i in JamSession's sequence.
export function chordElements() {
    return Array.from(document.querySelectorAll(`${SONG_SELECTOR} .open-chords`))
        .filter(el => parseChord(el.getAttribute('data-chord')));
}

// The line (a direct child of the song body) an element or text node is on.
function lineOf(node) {
    let el = node && node.nodeType === Node.ELEMENT_NODE ? node : node && node.parentElement;
    while (el && el.parentElement && !el.parentElement.matches(SONG_SELECTOR)) {
        el = el.parentElement;
    }
    return el && el.parentElement ? el : null;
}

// The chords on the lines the user has selected text on, as { start, end }
// positions, or null when nothing in the song is selected.
export function selectedSection() {
    const selection = window.getSelection && window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    const first = lineOf(range.startContainer);
    const last = lineOf(range.endContainer);
    if (!first || !last || first.parentElement !== last.parentElement) return null;
    const lines = Array.from(first.parentElement.children);
    const from = lines.indexOf(first);
    const to = lines.indexOf(last);
    const chosen = new Set(lines.slice(Math.min(from, to), Math.max(from, to) + 1));
    const positions = [];
    chordElements().forEach((el, i) => {
        if (chosen.has(lineOf(el))) positions.push(i);
    });
    return positions.length ? { start: positions[0], end: positions[positions.length - 1] } : null;
}

export function clearSelection() {
    const selection = window.getSelection && window.getSelection();
    if (selection) selection.removeAllRanges();
}

// Shade the lines of the looped part.
export function markSection(section) {
    document.querySelectorAll('.practice-loop-line').forEach(el => el.classList.remove('practice-loop-line'));
    if (!section) return;
    chordElements().slice(section.start, section.end + 1).forEach(el => {
        const line = lineOf(el);
        if (line) line.classList.add('practice-loop-line');
    });
}

// Mark the chord being played (and lightly the one after it).
export function markChords(position, nextPosition) {
    document.querySelectorAll('.backing-track-current, .practice-next').forEach(el => el.classList.remove('backing-track-current', 'practice-next'));
    const elements = chordElements();
    const current = position >= 0 ? elements[position] : null;
    const next = nextPosition >= 0 && nextPosition !== position ? elements[nextPosition] : null;
    if (current) current.classList.add('backing-track-current');
    if (next) next.classList.add('practice-next');
    return current;
}

// Top of the part of the window the song can be read in: below the site's
// navigation and the song's settings bar while they're pinned to the top.
function visibleTop() {
    let top = 0;
    document.querySelectorAll('nav.navbar, .settings-bar-container.fixed .settings-bar').forEach(el => {
        top = Math.max(top, el.getBoundingClientRect().bottom);
    });
    return top + 12;
}

// Keep `el` in view between the top bars and the dock. The dock's bar is
// usually at the bottom, but can have been moved to the top or a side, so the
// song is read on whichever side of it has more room, if it's over the song
// at all. Likewise an open panel that covers `el`, when that leaves room.
export function scrollIntoView(el) {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let top = visibleTop();
    let bottom = window.innerHeight - 16;
    const avoid = (selector, over, room) => {
        const part = document.querySelector(selector);
        if (!part) return;
        const box = part.getBoundingClientRect();
        if (box.left >= over.right || box.right <= over.left) return;
        const above = box.top - 16 - top;
        const below = bottom - (box.bottom + 16);
        if (above >= below) {
            if (above >= room) bottom = Math.min(bottom, box.top - 16);
        } else if (below >= room) {
            top = Math.max(top, box.bottom + 16);
        }
    };
    const song = document.querySelector(SONG_SELECTOR);
    avoid('.practice-dock-bar', song ? song.getBoundingClientRect() : rect, 0);
    avoid('.practice-dock-panel', rect, 80);
    if (rect.top < top || rect.bottom > bottom) {
        window.scrollBy({ top: rect.top - (top + (bottom - top) * 0.3), behavior: 'smooth' });
    }
}
